/** Exact `properties.name` strings from us-atlas@3/states-10m + click_that_hood canada.geojson */

export const US_ATLAS_REGION_NAMES = [
  'Alabama',
  'Alaska',
  'American Samoa',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Commonwealth of the Northern Mariana Islands',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Guam',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'United States Virgin Islands',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const;

export const CANADA_GEOJSON_REGION_NAMES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon Territory',
] as const;

const US_STATE_ABBREV: Record<string, string> = {
  al: 'Alabama',
  ak: 'Alaska',
  az: 'Arizona',
  ar: 'Arkansas',
  ca: 'California',
  co: 'Colorado',
  ct: 'Connecticut',
  de: 'Delaware',
  fl: 'Florida',
  ga: 'Georgia',
  hi: 'Hawaii',
  id: 'Idaho',
  il: 'Illinois',
  in: 'Indiana',
  ia: 'Iowa',
  ks: 'Kansas',
  ky: 'Kentucky',
  la: 'Louisiana',
  me: 'Maine',
  md: 'Maryland',
  ma: 'Massachusetts',
  mi: 'Michigan',
  mn: 'Minnesota',
  ms: 'Mississippi',
  mo: 'Missouri',
  mt: 'Montana',
  ne: 'Nebraska',
  nv: 'Nevada',
  nh: 'New Hampshire',
  nj: 'New Jersey',
  nm: 'New Mexico',
  ny: 'New York',
  nc: 'North Carolina',
  nd: 'North Dakota',
  oh: 'Ohio',
  ok: 'Oklahoma',
  or: 'Oregon',
  pa: 'Pennsylvania',
  ri: 'Rhode Island',
  sc: 'South Carolina',
  sd: 'South Dakota',
  tn: 'Tennessee',
  tx: 'Texas',
  ut: 'Utah',
  vt: 'Vermont',
  va: 'Virginia',
  wa: 'Washington',
  wv: 'West Virginia',
  wi: 'Wisconsin',
  wy: 'Wyoming',
  dc: 'District of Columbia',
};

const CA_ABBREV: Record<string, string> = {
  ab: 'Alberta',
  bc: 'British Columbia',
  mb: 'Manitoba',
  nb: 'New Brunswick',
  nl: 'Newfoundland and Labrador',
  ns: 'Nova Scotia',
  nt: 'Northwest Territories',
  nu: 'Nunavut',
  on: 'Ontario',
  pe: 'Prince Edward Island',
  qc: 'Quebec',
  sk: 'Saskatchewan',
  yt: 'Yukon Territory',
};

/** Lowercase alternate label -> exact geography `properties.name` */
const EXTRA_ALIASES: Record<string, string> = {
  // Canada
  yukon: 'Yukon Territory',
  pei: 'Prince Edward Island',
  'newfoundland': 'Newfoundland and Labrador',
  'newfoundland & labrador': 'Newfoundland and Labrador',
  'newfoundland and labrador': 'Newfoundland and Labrador',
  // US / DC
  'washington dc': 'District of Columbia',
  'washington d c': 'District of Columbia',
  'washington, dc': 'District of Columbia',
  'd c': 'District of Columbia',
  // Territories / long names
  'northern mariana islands': 'Commonwealth of the Northern Mariana Islands',
  cnmi: 'Commonwealth of the Northern Mariana Islands',
  mp: 'Commonwealth of the Northern Mariana Islands',
  'us virgin islands': 'United States Virgin Islands',
  'u s virgin islands': 'United States Virgin Islands',
  'virgin islands': 'United States Virgin Islands',
  vi: 'United States Virgin Islands',
  as: 'American Samoa',
  gu: 'Guam',
  pr: 'Puerto Rico',
};

function normalizeInput(raw: string): string {
  return raw
    .trim()
    .replace(/^\ufeff/, '')
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

const nameLowerToProper = new Map<string, string>();

for (const n of US_ATLAS_REGION_NAMES) {
  nameLowerToProper.set(n.toLowerCase(), n);
}
for (const n of CANADA_GEOJSON_REGION_NAMES) {
  nameLowerToProper.set(n.toLowerCase(), n);
}

export const DEFAULT_REGION_GREY = '#d8d8d8';

/**
 * Map pasted region labels to the exact `properties.name` used by our geography files.
 */
export function resolveRegionName(input: string): string | null {
  const key = normalizeInput(input);
  if (!key) return null;

  if (nameLowerToProper.has(key)) {
    return nameLowerToProper.get(key)!;
  }
  if (US_STATE_ABBREV[key]) {
    return US_STATE_ABBREV[key];
  }
  if (CA_ABBREV[key]) {
    return CA_ABBREV[key];
  }
  if (EXTRA_ALIASES[key]) {
    return EXTRA_ALIASES[key];
  }
  return null;
}

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function parseHexColor(cell: string): string | null {
  const t = cell.trim();
  if (!t) return null;
  const m = t.match(HEX_RE);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${h.toLowerCase()}`;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** Hue in degrees (any real, wrapped), s/l as 0–100 */
function hslToHex(hDeg: number, sPct: number, lPct: number): string {
  const s = clamp01(sPct / 100);
  const l = clamp01(lPct / 100);
  const h = ((hDeg % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) {
    rp = c;
    gp = x;
  } else if (h < 120) {
    rp = x;
    gp = c;
  } else if (h < 180) {
    gp = c;
    bp = x;
  } else if (h < 240) {
    gp = x;
    bp = c;
  } else if (h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  const R = Math.round((rp + m) * 255);
  const G = Math.round((gp + m) * 255);
  const B = Math.round((bp + m) * 255);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(R)}${toHex(G)}${toHex(B)}`;
}

function parseHueToken(token: string): number | null {
  const t = token.trim().toLowerCase();
  const m = t.match(/^([\d.]+)(deg|grad|rad|turn)?$/);
  if (!m) return null;
  let v = parseFloat(m[1]);
  if (!Number.isFinite(v)) return null;
  const unit = m[2] || 'deg';
  if (unit === 'rad') v *= 180 / Math.PI;
  else if (unit === 'grad') v *= 0.9;
  else if (unit === 'turn') v *= 360;
  return v;
}

function parseAlphaToken(token: string): number | null {
  const t = token.trim();
  if (t.endsWith('%')) {
    const n = parseFloat(t.slice(0, -1));
    if (!Number.isFinite(n)) return null;
    return clamp01(n / 100);
  }
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return clamp01(n);
}

/**
 * Parse `hsl()` / `hsla()` — comma syntax or space + optional `/ alpha` (CSS Color 4).
 * Opaque colors become lowercase `#rrggbb`. With alpha &lt; 1 returns `hsla(...)` for SVG.
 */
export function parseHslColor(cell: string): string | null {
  const raw = cell.trim();
  if (!raw || !/^hsla?\(/i.test(raw)) return null;

  // hsla(h, s%, l%, a) or hsl(h, s%, l%)
  const comma = raw.match(
    /^hsla?\(\s*([^,]+)\s*,\s*([\d.]+)\s*%\s*,\s*([\d.]+)\s*%\s*(?:,\s*([^)]+))?\s*\)\s*$/i
  );
  if (comma) {
    const h = parseHueToken(comma[1]);
    const s = parseFloat(comma[2]);
    const l = parseFloat(comma[3]);
    if (h === null || !Number.isFinite(s) || !Number.isFinite(l)) return null;
    const aPart = comma[4]?.trim();
    if (aPart !== undefined && aPart !== '') {
      const a = parseAlphaToken(aPart);
      if (a === null) return null;
      if (a < 1) {
        const hn = ((h % 360) + 360) % 360;
        const sPct = Math.min(100, Math.max(0, s));
        const lPct = Math.min(100, Math.max(0, l));
        return `hsla(${hn}, ${sPct}%, ${lPct}%, ${a})`;
      }
    }
    return hslToHex(h, s, l);
  }

  // hsl(h s% l%) or hsl(h s% l% / a)
  const space = raw.match(
    /^hsla?\(\s*([^\s/]+)\s+([\d.]+)\s*%\s+([\d.]+)\s*%\s*(?:\/\s*([^)]+))?\s*\)\s*$/i
  );
  if (space) {
    const h = parseHueToken(space[1]);
    const s = parseFloat(space[2]);
    const l = parseFloat(space[3]);
    if (h === null || !Number.isFinite(s) || !Number.isFinite(l)) return null;
    const aPart = space[4]?.trim();
    if (aPart !== undefined && aPart !== '') {
      const a = parseAlphaToken(aPart);
      if (a === null) return null;
      if (a < 1) {
        const hn = ((h % 360) + 360) % 360;
        const sPct = Math.min(100, Math.max(0, s));
        const lPct = Math.min(100, Math.max(0, l));
        return `hsla(${hn}, ${sPct}%, ${lPct}%, ${a})`;
      }
    }
    return hslToHex(h, s, l);
  }

  return null;
}

/** Accepts `#RRGGBB` / `#RGB` or `hsl()` / `hsla()` (comma or space-slash forms). */
export function parseTableColor(cell: string): string | null {
  return parseHexColor(cell) ?? parseHslColor(cell);
}

export function looksLikeFrameColor(cell: string): boolean {
  return parseTableColor(cell) !== null;
}

export type DataEntryMode = 'easy' | 'flexible';

export interface EasyScaleOptions {
  /** Inclusive minimum allowed value (defaults to 0). */
  min?: number;
  /** Inclusive maximum allowed value (optional). */
  max?: number;
  /** Denominator used in hue mapping (defaults to 1000). */
  scaleFactor?: number;
  /** Hue ramp selection for Easy mode. */
  colorRange?: 'rgb' | 'bpr';
}

function parseEasyScaleColor(cell: string, opts?: EasyScaleOptions): string | null {
  const t = cell.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  const min = opts?.min ?? 0;
  const max = opts?.max;
  const sf = opts?.scaleFactor ?? 1000;
  if (!Number.isFinite(min)) return null;
  if (max !== undefined && !Number.isFinite(max)) return null;
  if (!Number.isFinite(sf) || sf <= 0) return null;

  if (n < min) return null;
  if (max !== undefined && n > max) return null;

  const t01 = (n - min) / sf;
  const range = opts?.colorRange ?? 'rgb';
  const hue =
    range === 'bpr'
      ? 230 + 130 * t01 // ~Blue (230) -> Purple -> ~Red (360)
      : 256 * t01; // Red -> Green -> Blue (existing)
  // Keep as hsl() to exactly match the requested scale definition.
  return `hsl(${hue}, 67%, 56%)`;
}

export function parseTablePaint(
  cell: string,
  mode: DataEntryMode,
  opts?: EasyScaleOptions
): string | null {
  if (mode === 'easy') return parseEasyScaleColor(cell, opts);
  return parseTableColor(cell);
}

export function looksLikeFrameValue(
  cell: string,
  mode: DataEntryMode,
  opts?: EasyScaleOptions
): boolean {
  return parseTablePaint(cell, mode, opts) !== null;
}

export interface ChoroplethTableParse {
  frameLabels: string[];
  /** One object per animation frame: geography name -> CSS color (hex or hsla) */
  frames: Record<string, string>[];
  /** Trimmed pasted cell text per frame (for map overlays), keyed by geography `properties.name`. */
  rawFrameCells: Record<string, string>[];
  unmatchedLabels: string[];
}

/** Round a finite number to `significantDigits` significant digits (for map overlays). */
export function formatToSignificantDigits(value: number, significantDigits: number): string {
  if (!Number.isFinite(value)) return '';
  if (value === 0) return '0';
  const p = significantDigits;
  const sig = Math.pow(10, p - 1 - Math.floor(Math.log10(Math.abs(value))));
  const rounded = Math.round(value * sig) / sig;
  return String(rounded);
}

/** Format a pasted cell for display on the map (2 sig figs when numeric). */
export function formatOverlayCellValue(raw: string, _mode: DataEntryMode): string {
  const t = raw.trim();
  if (!t) return '';
  const n = Number(t);
  if (Number.isFinite(n)) return formatToSignificantDigits(n, 2);
  if (t.length > 18) return `${t.slice(0, 16)}…`;
  return t;
}

/**
 * Column 0: region. Columns 1..n: color for frames 0..n-1 (hex or hsl/hsla).
 * Header row: if column 1 is not a recognized color, row 0 is treated as labels (optional).
 */
function splitTableRow(line: string): string[] {
  const trimmed = line.trim();
  if (trimmed.includes('\t')) {
    return trimmed.split('\t').map((c) => c.trim());
  }
  const out: string[] = [];
  let cur = '';
  let depth = 0;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function parseMaybeNumber(cell: string): number | null {
  const t = cell.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return n;
}

/**
 * For Easy mode: scans the table and finds min/max numeric values across all frame columns.
 * Attempts to skip a header row if the first row's column 2 is not numeric.
 */
export function scanEasyTableRange(text: string): { min: number; max: number } | null {
  const raw = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let lines = raw
    .split('\n')
    .map((l) => l.replace(/\ufeff/g, ''))
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) return null;

  // Row 1 is always the header row (column labels).
  lines = lines.slice(1);
  if (lines.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (const line of lines) {
    const values = splitTableRow(line);
    for (let i = 1; i < values.length; i++) {
      const n = parseMaybeNumber(values[i]);
      if (n === null) continue;
      if (n < min) min = n;
      if (n > max) max = n;
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

export function parseChoroplethTable(
  text: string,
  opts?: { mode?: DataEntryMode; easy?: EasyScaleOptions }
): ChoroplethTableParse {
  const mode: DataEntryMode = opts?.mode ?? 'flexible';
  const easy = opts?.easy;
  const raw = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let lines = raw
    .split('\n')
    .map((l) => l.replace(/\ufeff/g, ''))
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    return { frameLabels: [], frames: [], rawFrameCells: [], unmatchedLabels: [] };
  }

  const splitRow = splitTableRow;

  // Row 1 is always the header row (column labels).
  const header = splitRow(lines[0]);
  if (header.length < 2) {
    return { frameLabels: [], frames: [], rawFrameCells: [], unmatchedLabels: [] };
  }
  let frameLabels: string[] = header.slice(1);
  lines = lines.slice(1);
  if (lines.length === 0) {
    return { frameLabels, frames: [], rawFrameCells: [], unmatchedLabels: [] };
  }

  let maxFrames = 0;
  for (const line of lines) {
    const v = splitRow(line);
    maxFrames = Math.max(maxFrames, Math.max(0, v.length - 1));
  }

  if (maxFrames === 0) {
    return { frameLabels: [], frames: [], rawFrameCells: [], unmatchedLabels: [] };
  }

  // Prefer the number of header columns as the intended frame count.
  maxFrames = Math.max(maxFrames, frameLabels.length);

  if (frameLabels.length < maxFrames) {
    frameLabels = [
      ...frameLabels,
      ...Array.from({ length: maxFrames - frameLabels.length }, (_, i) => `Frame ${frameLabels.length + i + 1}`),
    ];
  } else if (frameLabels.length > maxFrames) {
    frameLabels = frameLabels.slice(0, maxFrames);
  }

  const frames: Record<string, string>[] = Array.from({ length: maxFrames }, () => ({}));
  const rawFrameCells: Record<string, string>[] = Array.from({ length: maxFrames }, () => ({}));
  const unmatchedSet = new Set<string>();

  for (const line of lines) {
    const values = splitRow(line);
    if (values.length < 2) continue;
    const regionCell = values[0];
    const proper = resolveRegionName(regionCell);
    if (!proper) {
      unmatchedSet.add(regionCell);
      continue;
    }
    for (let f = 0; f < maxFrames; f++) {
      const col = values[f + 1];
      if (col === undefined || col === '') continue;
      const trimmed = col.trim();
      rawFrameCells[f][proper] = trimmed;
      const paint = parseTablePaint(trimmed, mode, easy);
      if (paint) {
        frames[f][proper] = paint;
      }
    }
  }

  return {
    frameLabels,
    frames,
    rawFrameCells,
    unmatchedLabels: Array.from(unmatchedSet),
  };
}
